import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { useAuth } from './AuthContext';

const SavedProductsContext = createContext(null);

export const useSavedProducts = () => {
  const context = useContext(SavedProductsContext);
  if (!context) throw new Error('useSavedProducts must be used within SavedProductsProvider');
  return context;
};

export function SavedProductsProvider({ children }) {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [savedForLater, setSavedForLater] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);

  const refreshSavedProducts = useCallback(async () => {
    if (!user) {
      setWishlist([]);
      setSavedForLater([]);
      return;
    }
    setLoadingSaved(true);
    try {
      const [wishlistData, savedData] = await Promise.all([
        api.getWishlist(),
        api.getSavedForLater(),
      ]);
      setWishlist(Array.isArray(wishlistData) ? wishlistData : []);
      setSavedForLater(Array.isArray(savedData) ? savedData : []);
    } finally {
      setLoadingSaved(false);
    }
  }, [user]);

  useEffect(() => {
    refreshSavedProducts().catch(() => {});
  }, [refreshSavedProducts]);

  const wishlistIds = useMemo(
    () => new Set(wishlist.map((item) => item.product_id)),
    [wishlist]
  );

  const isWishlisted = useCallback(
    (productId) => wishlistIds.has(productId),
    [wishlistIds]
  );

  const addToWishlist = useCallback(
    async (productId) => {
      const added = await api.addToWishlist(productId);
      setWishlist((prev) => {
        const exists = prev.some((item) => item.product_id === productId);
        if (exists) return prev.map((item) => (item.product_id === productId ? added : item));
        return [added, ...prev];
      });
      return added;
    },
    []
  );

  const removeFromWishlist = useCallback(async (productId) => {
    await api.removeFromWishlist(productId);
    setWishlist((prev) => prev.filter((item) => item.product_id !== productId));
  }, []);

  const toggleWishlist = useCallback(
    async (productId) => {
      if (isWishlisted(productId)) {
        await removeFromWishlist(productId);
        return false;
      }
      await addToWishlist(productId);
      return true;
    },
    [addToWishlist, isWishlisted, removeFromWishlist]
  );

  const addToSavedForLater = useCallback(async (productId, quantity = 1) => {
    const saved = await api.addToSavedForLater(productId, quantity);
    setSavedForLater((prev) => {
      const exists = prev.some((item) => item.product_id === productId);
      if (exists) return prev.map((item) => (item.product_id === productId ? saved : item));
      return [saved, ...prev];
    });
    return saved;
  }, []);

  const removeFromSavedForLater = useCallback(async (productId) => {
    await api.removeFromSavedForLater(productId);
    setSavedForLater((prev) => prev.filter((item) => item.product_id !== productId));
  }, []);

  const updateSavedForLaterQuantity = useCallback(async (productId, quantity) => {
    const updated = await api.updateSavedForLater(productId, quantity);
    setSavedForLater((prev) =>
      prev.map((item) => (item.product_id === productId ? updated : item))
    );
    return updated;
  }, []);

  return (
    <SavedProductsContext.Provider
      value={{
        wishlist,
        savedForLater,
        loadingSaved,
        isWishlisted,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        addToSavedForLater,
        removeFromSavedForLater,
        updateSavedForLaterQuantity,
        refreshSavedProducts,
      }}
    >
      {children}
    </SavedProductsContext.Provider>
  );
}
